import React from 'react';
import Header from '../../components/Header';
import { Shield, Lock, Database, Eye, AlertTriangle, CheckCircle, ArrowLeft, Key, Server, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo } from '../../src/seo/Seo';

const SecurityDataProtection: React.FC = () => {
  // Fonctions par défaut pour les props du Header
  const handleDownloadClick = () => {
    // Rediriger vers la page d'accueil pour le téléchargement
    window.location.href = '/';
  };

  const handleMySpaceClick = () => {
    // Rediriger vers la page d'accueil pour My Space
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      <Seo
        title={{ fr: 'CGU — Sécurité & protection des données — Burok', en: 'Terms — Security & data protection — Burok' }}
        description={{
          fr: "Mesures de sécurité, protection des données et bonnes pratiques de confidentialité dans l'écosystème Burok.",
          en: 'Security measures, data protection, and privacy best practices across the Burok ecosystem.',
        }}
        canonicalPath={{ fr: '/cgu/security-data-protection', en: '/en/cgu/security-data-protection' }}
      />
      <Header onDownloadClick={handleDownloadClick} onMySpaceClick={handleMySpaceClick} />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-6">
            <Shield className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sécurité et Protection des Données
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Détails complets sur nos mesures de sécurité et la protection de vos informations
          </p>
          <Link
            to="/cgu"
            className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-medium rounded-full hover:bg-gray-700 transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux CGU
          </Link>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            
            {/* Security Overview */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 text-green-500 mr-3" />
                Vue d'Ensemble de la Sécurité
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                La sécurité de vos données est notre priorité absolue. Burok met en œuvre des mesures de sécurité 
                de niveau bancaire pour protéger vos informations personnelles et financières contre toute forme 
                de compromission ou d'accès non autorisé.
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                  <h3 className="text-xl font-semibold text-green-900 mb-4">Standards de Sécurité</h3>
                  <ul className="space-y-3 text-green-800">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Certification ISO 27001 pour la gestion de la sécurité</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Conformité SOC 2 Type II pour la sécurité des données</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Audits de sécurité trimestriels par des experts externes</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Tests de pénétration mensuels automatisés</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Surveillance continue 24h/24 et 7j/7</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Infrastructure Sécurisée</h3>
                  <ul className="space-y-3 text-blue-800">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Hébergement dans des centres de données certifiés Tier IV</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Réplication des données sur plusieurs sites géographiques</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Protection contre les catastrophes naturelles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Redondance des systèmes critiques</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Data Encryption */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Lock className="w-6 h-6 text-blue-500 mr-3" />
                Chiffrement et Protection des Données
              </h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                    <Key className="w-5 h-5 text-blue-600 mr-3" />
                    Chiffrement de Bout en Bout
                  </h3>
                  <p className="text-blue-800 mb-4">
                    Toutes vos données sont chiffrées à plusieurs niveaux pour garantir une protection maximale 
                    contre tout accès non autorisé.
                  </p>
                  <ul className="text-blue-800 space-y-2">
                    <li>• Chiffrement AES-256 pour les données au repos</li>
                    <li>• Chiffrement TLS 1.3 pour les données en transit</li>
                    <li>• Clés de chiffrement uniques par utilisateur</li>
                    <li>• Rotation automatique des clés de chiffrement</li>
                    <li>• Chiffrement des sauvegardes et archives</li>
                  </ul>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-purple-900 mb-4 flex items-center">
                    <Database className="w-5 h-5 text-purple-600 mr-3" />
                    Protection des Données Sensibles
                  </h3>
                  <p className="text-purple-800 mb-4">
                    Les informations financières et personnelles sont traitées avec des mesures de sécurité 
                    supplémentaires et des contrôles d'accès stricts.
                  </p>
                  <ul className="text-purple-800 space-y-2">
                    <li>• Hachage des mots de passe avec bcrypt (coût 12)</li>
                    <li>• Masquage des données sensibles dans les logs</li>
                    <li>• Tokenisation des informations bancaires</li>
                    <li>• Anonymisation des données d'analyse</li>
                    <li>• Suppression automatique des données temporaires</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Access Control */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Eye className="w-6 h-6 text-indigo-500 mr-3" />
                Contrôle d'Accès et Authentification
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-indigo-900 mb-4">Authentification Multi-Facteurs</h3>
                  <ul className="text-indigo-800 space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Authentification par SMS ou application TOTP</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Biométrie sur les appareils compatibles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Clés de sécurité FIDO2/U2F</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Authentification adaptative selon le risque</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-cyan-900 mb-4">Gestion des Sessions</h3>
                  <ul className="text-cyan-800 space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-cyan-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Tokens JWT avec expiration automatique</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-cyan-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Déconnexion automatique après inactivité</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-cyan-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Limitation du nombre de sessions simultanées</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-cyan-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Révocation des sessions à distance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Monitoring and Incident Response */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Server className="w-6 h-6 text-orange-500 mr-3" />
                Surveillance et Gestion des Incidents
              </h2>
              
              <div className="space-y-6">
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-orange-900 mb-4">Surveillance Continue</h3>
                  <p className="text-orange-800 mb-4">
                    Notre équipe de sécurité surveille en permanence nos systèmes pour détecter et 
                    prévenir toute activité suspecte ou tentative d'intrusion.
                  </p>
                  <ul className="text-orange-800 space-y-2">
                    <li>• Détection d'intrusion en temps réel (IDS/IPS)</li>
                    <li>• Analyse comportementale des utilisateurs</li>
                    <li>• Surveillance des accès aux données sensibles</li>
                    <li>• Alertes automatiques pour activités anormales</li>
                    <li>• Corrélation des événements de sécurité</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-red-900 mb-4 flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
                    Gestion des Incidents de Sécurité
                  </h3>
                  <p className="text-red-800 mb-4">
                    En cas d'incident de sécurité, nous disposons d'une procédure de réponse 
                    structurée et d'une équipe dédiée disponible 24h/24.
                  </p>
                  <ul className="text-red-800 space-y-2">
                    <li>• Équipe de réponse aux incidents disponible 24h/24</li>
                    <li>• Procédures de confinement et d'éradication</li>
                    <li>• Communication transparente avec les utilisateurs</li>
                    <li>• Analyse post-incident et amélioration continue</li>
                    <li>• Notification aux autorités compétentes si nécessaire</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Compliance and Certifications */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Globe className="w-6 h-6 text-green-500 mr-3" />
                Conformité et Certifications
              </h2>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <p className="text-green-800 mb-6">
                  Burok respecte et dépasse les standards internationaux de sécurité et de protection des données, 
                  garantissant ainsi la conformité avec les réglementations les plus strictes.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-green-900 mb-3">Certifications Internationales</h4>
                    <ul className="text-green-800 space-y-2 text-sm">
                      <li>• ISO 27001 : Gestion de la sécurité de l'information</li>
                      <li>• SOC 2 Type II : Contrôles de sécurité et disponibilité</li>
                      <li>• PCI DSS : Sécurité des données de cartes de paiement</li>
                      <li>• GDPR : Conformité au règlement européen</li>
                      <li>• eIDAS : Services de confiance électronique</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-green-900 mb-3">Audits et Vérifications</h4>
                    <ul className="text-green-800 space-y-2 text-sm">
                      <li>• Audits de sécurité trimestriels</li>
                      <li>• Tests de pénétration mensuels</li>
                      <li>• Vérifications de conformité RGPD</li>
                      <li>• Évaluations de risques continues</li>
                      <li>• Contrôles par des organismes tiers</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Questions sur la Sécurité ?
              </h2>
              <p className="text-gray-600 mb-6">
                Notre équipe de sécurité est disponible pour répondre à toutes vos questions 
                sur la protection de vos données et nos mesures de sécurité.
              </p>
              <div className="space-x-4">
                <a
                  href="mailto:security@burok.app"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium rounded-full hover:shadow-lg hover:scale-105 transition-all duration-200"
                >
                  Contacter la Sécurité
                </a>
                <Link
                  to="/cgu"
                  className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-medium rounded-full hover:bg-gray-700 transition-all duration-200"
                >
                  Retour aux CGU
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default SecurityDataProtection;
